/**
 * Shared utilities for converting between UI weekday indices and RRULE weekday indices.
 * 
 * This module provides consistent weekday conversion logic used by both frontend and backend
 * to handle different week start configurations (Sunday-first vs Monday-first).
 * 
 * RRULE Weekday Mapping (RFC 5545):
 * - Monday = 0, Tuesday = 1, Wednesday = 2, Thursday = 3, Friday = 4, Saturday = 5, Sunday = 6
 * 
 * UI Weekday Mapping (depends on startDayAtWeek):
 * - startDayAtWeek = 0 (Sunday-first): Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
 * - startDayAtWeek = 1 (Monday-first): Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
 */

export type WeekStartDay = 0 | 1;

/**
 * Converts UI weekday indices to RRULE weekday indices.
 * 
 * @param uiValues - Array of UI weekday indices (0-6)
 * @param startDayAtWeek - Week start configuration: 0=Sunday-first, 1=Monday-first
 * @returns Array of RRULE weekday indices (0-6)
 * 
 * @example
 * // Sunday-first calendar: UI Sunday=0, Monday=1, ..., Saturday=6
 * convertUIToRRuleWeekdays([0, 1], 0) // Returns [6, 0] (Sunday, Monday)
 * 
 * // Monday-first calendar: UI Monday=0, Tuesday=1, ..., Sunday=6  
 * convertUIToRRuleWeekdays([0, 1], 1) // Returns [0, 1] (Monday, Tuesday)
 */
export function convertUIToRRuleWeekdays(uiValues: number[], startDayAtWeek: WeekStartDay): number[] {
  if (startDayAtWeek === 0) {
    // Sunday start: Convert UI values to RRule weekdays
    // UI:   Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
    // RRule: Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
    return uiValues.map(uiValue => {
      // Convert UI value to RRule weekday for Sunday start
      return uiValue === 0 ? 6 : uiValue - 1; // Sunday(0)→6, Monday(1)→0, Tuesday(2)→1, etc.
    });
  }
  
  // Monday start: UI values map directly to RRule weekdays
  return uiValues;
}

/**
 * Converts RRULE weekday indices to UI weekday indices.
 * 
 * @param rruleValues - Array of RRULE weekday indices (0-6)
 * @param startDayAtWeek - Week start configuration: 0=Sunday-first, 1=Monday-first
 * @returns Array of UI weekday indices (0-6)
 * 
 * @example
 * // Sunday-first calendar: RRULE Sunday=6, Monday=0, ..., Saturday=5
 * convertRRuleToUIWeekdays([6, 0], 0) // Returns [0, 1] (Sunday, Monday)
 * 
 * // Monday-first calendar: RRULE Monday=0, Tuesday=1, ..., Sunday=6
 * convertRRuleToUIWeekdays([0, 1], 1) // Returns [0, 1] (Monday, Tuesday)
 */
export function convertRRuleToUIWeekdays(rruleValues: number[], startDayAtWeek: WeekStartDay): number[] {
  if (startDayAtWeek === 0) {
    // Sunday start: Convert RRule weekdays to UI values
    // RRule: Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
    // UI:   Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
    return rruleValues.map(rruleValue => {
      // Convert RRule weekday to UI value for Sunday start
      return rruleValue === 6 ? 0 : rruleValue + 1; // Sunday(6)→0, Monday(0)→1, Tuesday(1)→2, etc.
    });
  }
  
  // Monday start: RRule values map directly to UI values
  return rruleValues;
}

/**
 * Gets the weekday name for display purposes.
 * 
 * @param weekdayIndex - RRULE weekday index (0-6)
 * @param startDayAtWeek - Week start configuration: 0=Sunday-first, 1=Monday-first
 * @returns Human-readable weekday name
 */
export function getWeekdayName(weekdayIndex: number, startDayAtWeek: WeekStartDay): string {
  const weekdayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return weekdayNames[weekdayIndex];
}

/**
 * Gets the weekday abbreviation for display purposes.
 * 
 * @param weekdayIndex - RRULE weekday index (0-6)
 * @param startDayAtWeek - Week start configuration: 0=Sunday-first, 1=Monday-first
 * @returns Short weekday abbreviation (Mon, Tue, etc.)
 */
export function getWeekdayAbbreviation(weekdayIndex: number, startDayAtWeek: WeekStartDay): string {
  const weekdayAbbrevs = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return weekdayAbbrevs[weekdayIndex];
}

/**
 * Gets the weekday buttons configuration for UI display.
 * 
 * @param startDayAtWeek - Week start configuration: 0=Sunday-first, 1=Monday-first
 * @returns Array of weekday button objects with label and UI index
 * 
 * @example
 * // Sunday-first calendar
 * getSortedWeekdayButtons(0) // Returns [{label: 's', value: 0}, {label: 'm', value: 1}, ...]
 * 
 * // Monday-first calendar  
 * getSortedWeekdayButtons(1) // Returns [{label: 'm', value: 0}, {label: 't', value: 1}, ...]
 */
export function getSortedWeekdayButtons(startDayAtWeek: WeekStartDay) {
  // Define weekday labels in UI order
  const weekdayLabels = startDayAtWeek === 0 
    ? ['s', 'm', 't', 'w', 't', 'f', 's'] // Sunday-first: Sun, Mon, Tue, Wed, Thu, Fri, Sat
    : ['m', 't', 'w', 't', 'f', 's', 's']; // Monday-first: Mon, Tue, Wed, Thu, Fri, Sat, Sun

  // Create buttons with UI indices (0-6) as values
  const weekdays = weekdayLabels.map((label, uiIndex) => ({
    label,
    value: uiIndex // UI index directly
  }));

  return weekdays;
}

/**
 * Returns true iff every element of `indices` is an integer in [0, 6]
 * (a valid weekday index in either UI or RRule space). Used by reducers
 * and tests to reject malformed schedule payloads early.
 */
export function validateWeekdayIndices(indices: readonly number[]): boolean {
  return indices.every((i) => Number.isInteger(i) && i >= 0 && i <= 6);
}

